import { ActionTree, GetterTree, MutationTree, Module } from "vuex"
import { RootState } from "@/store/RootStore"
import { make } from "vuex-pathify"
import Services from "@/services/Services"
import { DraftInvoiceResponse } from "@/services/PaymentService"

export const namespaced: boolean = true

export type PaymentStatus = "idle" | "initializing" | "ready" | "processing" | "success" | "error"

export interface PaymentState {
    orderId?: string
    paymentStatus: PaymentStatus
    stripeError?: string
    draftInvoiceData?: DraftInvoiceResponse
}

function defaultState(): PaymentState {
    return {
        orderId: undefined,
        paymentStatus: "idle",
        stripeError: undefined,
        draftInvoiceData: undefined
    }
}

export const state: PaymentState = defaultState()

//===================================
// Getters
//===================================
export const getters: GetterTree<PaymentState, RootState> = {}

//===================================
// Actions
//===================================
export const actions: ActionTree<PaymentState, RootState> = {
    ...make.actions(state),

    resetPayment({ commit }) {
        commit("resetState")
    },

    async initPayment({ dispatch }, orderId: string): Promise<DraftInvoiceResponse> {
        dispatch("setOrderId", orderId)
        dispatch("setPaymentStatus", "initializing" as PaymentStatus)
        dispatch("setStripeError", undefined)
        try {
            const data = await Services.payments.draftInvoice(orderId)
            dispatch("setDraftInvoiceData", data)
            dispatch("setPaymentStatus", "ready" as PaymentStatus)
            return data
        } catch (error: any) {
            const message = error?.response?.data?.errorMessage || error?.message || "Failed to initialize payment."
            dispatch("setStripeError", message)
            dispatch("setPaymentStatus", "error" as PaymentStatus)
            throw error
        }
    },

    async refreshPayment({ dispatch, state }): Promise<DraftInvoiceResponse> {
        if (!state.orderId) throw new Error("No orderId set for payment refresh.")
        dispatch("setPaymentStatus", "initializing" as PaymentStatus)
        dispatch("setStripeError", undefined)
        try {
            const data = await Services.payments.refreshPayment(state.orderId)
            dispatch("setDraftInvoiceData", data)
            dispatch("setPaymentStatus", "ready" as PaymentStatus)
            return data
        } catch (error: any) {
            const message = error?.response?.data?.errorMessage || error?.message || "Failed to refresh payment."
            dispatch("setStripeError", message)
            dispatch("setPaymentStatus", "error" as PaymentStatus)
            throw error
        }
    },

    async capturePaymentMethod({ dispatch, state }): Promise<void> {
        if (!state.orderId) throw new Error("No orderId set for payment capture.")
        dispatch("setPaymentStatus", "processing" as PaymentStatus)
        try {
            await Services.payments.capturePaymentMethod(state.orderId)
        } catch (error: any) {
            const message = error?.response?.data?.errorMessage || error?.message || "Failed to capture payment method."
            dispatch("setStripeError", message)
            dispatch("setPaymentStatus", "error" as PaymentStatus)
            throw error
        }
    },

    async submitPayment({ dispatch, state }): Promise<void> {
        if (!state.orderId) throw new Error("No orderId set for payment submission.")
        try {
            await Services.payments.submitOrder(state.orderId)
            dispatch("setPaymentStatus", "success" as PaymentStatus)
        } catch (error: any) {
            const message = error?.response?.data?.errorMessage || error?.message || "Failed to submit payment."
            dispatch("setStripeError", message)
            dispatch("setPaymentStatus", "error" as PaymentStatus)
            throw error
        }
    }
}

//===================================
// Mutations
//===================================
export const mutations: MutationTree<PaymentState> = {
    ...make.mutations(state),

    resetState(state) {
        Object.assign(state, defaultState())
    }
}

//===================================
// Module
//===================================
export const payment: Module<PaymentState, RootState> = {
    namespaced,
    state,
    getters,
    actions,
    mutations
}
